import React, { useState, useEffect, useRef } from "react";
import styles from "./MonthRangePicker.module.css";
import { DayPicker, DateRange } from "react-day-picker";
import { startOfMonth, endOfMonth, parse } from "date-fns";
import "react-day-picker/dist/style.css";

const parseMonth = (monthStr: string): Date | null => {
  if (!monthStr || !/^\d{4}_\d{2}$/.test(monthStr)) return null;
  return parse(monthStr, "yyyy_MM", new Date());
};

interface Props {
  availableMonths: string[]; // Expects "YYYY_MM"
  initialStartMonth?: string;
  initialEndMonth?: string;
  onRangeSelect: (fromYM: string, toYM: string) => void;
  disabledRange?: { start: string; end: string } | null;
  disableBefore?: string;
  disableAfter?: string;
  onRangeClear: () => void;
}

const parseYear = (ym: string) => parseInt(ym.split("_")[0], 10);
const parseMonthNum = (ym: string) => parseInt(ym.split("_")[1], 10);

const formatMonthDisplay = (ym: string) => {
  if (!ym) return "";
  const [year, month] = ym.split("_");
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString("en-US", { month: "short" });
};

const MonthRangePicker: React.FC<Props> = ({
  availableMonths,
  initialStartMonth,
  initialEndMonth,
  onRangeSelect,
  disabledRange,
  disableBefore,
  disableAfter,
  onRangeClear,
}) => {
  const allYears = Array.from(new Set(availableMonths.map(parseYear))).sort();

  const [viewYear, setViewYear] = useState<number>(() =>
    parseYear(
      initialEndMonth ||
        availableMonths[availableMonths.length - 1] ||
        `${new Date().getFullYear()}_01`
    )
  );
  const [startMonth, setStartMonth] = useState<string | null>(
    initialStartMonth || null
  );
  const [endMonth, setEndMonth] = useState<string | null>(
    initialEndMonth || null
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [warning, setWarning] = useState<string>("");

  useEffect(() => {
    setStartMonth(initialStartMonth || null);
    setEndMonth(initialEndMonth || null);
  }, [initialStartMonth, initialEndMonth]);

  useEffect(() => {
    if (!disabledRange?.start || !disabledRange?.end || !startMonth) {
      // No need to check if there's no disabled range or no active selection.
      return;
    }

    // If only a start month is selected, the range is just that month.
    const currentRangeStart = startMonth;
    const currentRangeEnd = endMonth || startMonth;

    // Check for overlap: (StartA <= EndB) and (EndA >= StartB)
    const isOverlapping =
      currentRangeStart <= disabledRange.end &&
      currentRangeEnd >= disabledRange.start;

    if (isOverlapping) {
      // The current selection is now invalid, so clear it.
      setWarning("Selection cleared due to a date conflict.");
      onRangeClear();
    }
    // We only want this to run when the disabledRange changes.
    // Assuming onRangeClear is stable (wrapped in useCallback in parent).
  }, [disabledRange, onRangeClear]);

  const handleMonthClick = (year: number, month: number) => {
    const clickedMonth = `${year}_${String(month).padStart(2, "0")}`;
    setIsOpen(true);

    if (!startMonth || (startMonth && endMonth)) {
      // Start a new selection
      setStartMonth(clickedMonth);
      setEndMonth(null);
    } else if (startMonth && !endMonth) {
      // Finish the selection
      const startDate = new Date(
        parseYear(startMonth),
        parseMonthNum(startMonth) - 1
      );
      const endDate = new Date(year, month - 1);

      if (endDate < startDate) {
        // If end is before start, swap them
        setStartMonth(clickedMonth);
        setEndMonth(startMonth);
        onRangeSelect(clickedMonth, startMonth);
      } else {
        setEndMonth(clickedMonth);
        onRangeSelect(startMonth, clickedMonth);
      }
    }
  };

  const changeYear = (direction: number) => {
    const newYear = viewYear + direction;
    if (allYears.includes(newYear)) {
      setViewYear(newYear);
    }
  };

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return (
    <div className={styles.pickerContainer}>
      <div className={styles.header}>
        <button
          onClick={() => changeYear(-1)}
          disabled={!allYears.includes(viewYear - 1)}
        >
          &larr;
        </button>
        <span>{viewYear}</span>
        <button
          onClick={() => changeYear(1)}
          disabled={!allYears.includes(viewYear + 1)}
        >
          &rarr;
        </button>
        <button className={styles.clearButton} onClick={onRangeClear}>
          Clear 
        </button>
      </div>
      <div className={styles.monthsGrid}>
        {months.map((month, index) => {
          const monthNumber = index + 1;
          const currentMonthYM = `${viewYear}_${String(monthNumber).padStart(
            2,
            "0"
          )}`;
          const isUnavailable = !availableMonths.includes(currentMonthYM);
          const isBefore = disableBefore
            ? currentMonthYM <= disableBefore
            : false;
          const isAfter = disableAfter ? currentMonthYM >= disableAfter : false;
          const isInDisabledRange = !!(
            disabledRange?.start &&
            disabledRange?.end &&
            currentMonthYM >= disabledRange.start &&
            currentMonthYM <= disabledRange.end
          );
          const isDisabled =
            isUnavailable || isBefore || isAfter || isInDisabledRange;

          let className = styles.monthButton;
          if (currentMonthYM === startMonth || currentMonthYM === endMonth) {
            className = `${styles.monthButton} ${styles.selected}`;
          } else if (
            startMonth &&
            endMonth &&
            currentMonthYM > startMonth &&
            currentMonthYM < endMonth
          ) {
            className = `${styles.monthButton} ${styles.inRange}`;
          }

          return (
            <div key={month} className={styles.tooltipContainer}>
            <button
              key={month}
              disabled={isDisabled}
              className={className}
              onClick={() => handleMonthClick(viewYear, monthNumber)}
            >
              {month}
            </button>
            <span className={styles.tooltipText}>
            Select a start and end month by clicking. Double-click to choose just one month.
          </span>
        </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthRangePicker;
